'use client'

import { useBackdropClose } from '@trustoo/components/modal/hooks/useBackdropClose'
import { useCancelToClose } from '@trustoo/components/modal/hooks/useCancelToClose'
import { useDialogTransition } from '@trustoo/components/modal/hooks/useDialogTransition'

interface UseAnimatedDialogParams {
    open: boolean
    onClose: () => void
    closeOnBackdropClick?: boolean
    transitionTimeout?: number
    onExited?: () => void
}

/**
 * Behaviour for `Dialog.AnimatedRoot`: drives the enter/exit transition via `useDialogTransition`,
 * routes Escape through `onClose`, and exposes the backdrop-click handler plus the current
 * transition status and mount flag. Keeps `AnimatedDialog` purely presentational.
 */
export const useAnimatedDialog = ({
    open,
    onClose,
    closeOnBackdropClick = false,
    transitionTimeout = 300,
    onExited,
}: UseAnimatedDialogParams) => {
    const { status, isMounted, dialogRef } = useDialogTransition({
        open,
        timeout: transitionTimeout,
        onExited,
    })

    useCancelToClose(dialogRef, onClose, isMounted)

    const onBackdropClick = useBackdropClose(dialogRef, onClose, closeOnBackdropClick)

    return { dialogRef, status, isMounted, onBackdropClick }
}
